import { motion, useAnimation } from 'motion/react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useDeletePost } from 'src/features/feeds/api/feeds/use-delete-post'
import { useToggleLike } from 'src/features/feeds/api/likes/use-toggle-like'
import { ActiveCommentIcon } from 'src/features/feeds/icons/active-comment-icon'
import { ActiveHeartIcon } from 'src/features/feeds/icons/active-heart-icon'
import { CommentIcon } from 'src/features/feeds/icons/comment-icon'
import { HeartIcon } from 'src/features/feeds/icons/heart-icon'
import { logger } from 'src/lib/logger'
import { ImageGrid } from 'src/shared/components/ui/media/image-grid'
import { TextWithLinks } from 'src/shared/components/ui/text-with-links'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import type { StreamPost } from 'src/shared/schemas/stream/feed'
import { cn } from 'src/shared/utils/cn'

import { PostHeader } from './post-header'

interface PostProps {
  post: StreamPost
  showFullContent?: boolean
  isPublic?: boolean
}

const Post = ({ post, showFullContent = false, isPublic = false }: PostProps) => {
  const author = post.actor
  const { isMobile } = useBreakpoint()
  const navigate = useNavigate()
  const toggleLike = useToggleLike()
  const deletePost = useDeletePost()
  const { toast } = useToast()
  const { data: user } = useMyUserData()

  const hasLiked = post.ownReactions?.like && post.ownReactions.like.length > 0
  const hasCommented = post.ownReactions?.comment && post.ownReactions.comment.length > 0
  const likeReactionId = hasLiked ? post.ownReactions?.like?.[0]?.id : undefined

  const likeCount = post.reactionCounts?.like || 0
  const commentCount = post.reactionCounts?.comment || 0

  const commentControls = useAnimation()
  const likeControls = useAnimation()
  const heartControls = useAnimation()

  const handleLike = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!user) return

    // Animate the button container lightly
    likeControls.start({ scale: [1, 1.08, 1] }, { duration: 0.28, ease: 'easeOut' })
    // Animate the heart icon more noticeably
    await heartControls.start({ scale: [1, 1.2, 1] }, { duration: 0.32, ease: 'easeOut' })

    const variables = {
      activityId: post.id,
      parentType: 'post' as const,
      reactionId: hasLiked ? likeReactionId : undefined,
      isLiked: Boolean(hasLiked),
    }

    toggleLike.mutate(variables)
  }

  const handlePostClick = () => {
    navigate(paths.feed.post.getHref(post.id), {
      state: {
        post,
        scrollPosition: window.scrollY,
      },
    })
  }

  const handleDelete = async () => {
    try {
      await deletePost.mutateAsync(post.id)
      toast({
        title: 'Success',
        description: 'Post deleted successfully',
        variant: 'default',
      })
      navigate(paths.feed.path)
    } catch (error) {
      logger.error('Post - Error deleting post:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete post. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleComment = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    await commentControls.start({ scale: [1, 1.12, 1] }, { duration: 0.18, ease: 'easeOut' })
    if (!showFullContent && !isPublic) {
      handlePostClick()
    }
  }

  if (!author) {
    logger.error('Post - No author data available for post:', post.id)
    return null
  }

  if (!author.profession) {
    logger.warn('Post - No profession data for author:', author.id)
  }

  return (
    <section className={cn('flex flex-col gap-4 overflow-visible border-b border-lightBorder bg-background py-8')}>
      <PostHeader
        userId={author.id}
        fullName={author.fullName}
        username={author.username}
        profession={author.profession as Profession}
        shortAddress={author.shortAddress}
        timestamp={post.time}
        clickable={!isPublic}
        post={post}
        onDelete={handleDelete}
      />

      {post.images && post.images.length > 0 && (
        <div onClick={(e) => e.stopPropagation()}>
          <ImageGrid images={post.images} maxImages={5} />
        </div>
      )}

      {post.text && (
        <TextWithLinks
          text={post.text || ''}
          className={cn(
            'text-foreground/90 max-w-full whitespace-pre-wrap break-words [word-break:break-word]',
            !showFullContent && !isPublic && 'line-clamp-6 cursor-pointer',
            isMobile && 'text-sm',
          )}
          onClick={isPublic ? undefined : handlePostClick}
        />
      )}

      <div
        className={cn(
          'flex items-center gap-4 overflow-visible text-muted',
          !showFullContent && !isPublic && 'cursor-pointer',
        )}
        onClick={!showFullContent && !isPublic ? handlePostClick : undefined}
      >
        <motion.button
          type="button"
          animate={likeControls}
          onClick={handleLike}
          style={{ transformOrigin: 'left center' }}
          className={cn(
            'flex items-center gap-1 overflow-visible text-sm',
            hasLiked ? 'font-semibold text-primary' : 'font-medium text-muted',
          )}
        >
          <motion.span
            animate={heartControls}
            style={{ transformOrigin: 'left center' }}
            className="inline-flex overflow-visible"
          >
            {hasLiked ? <ActiveHeartIcon size={16} /> : <HeartIcon size={16} />}
          </motion.span>
          <span>
            {likeCount} like{likeCount === 1 ? '' : 's'}
          </span>
        </motion.button>
        <motion.button
          type="button"
          animate={commentControls}
          onClick={handleComment}
          style={{ transformOrigin: 'left center' }}
          className={cn(
            'flex items-center gap-1 overflow-visible text-sm',
            hasCommented ? 'font-semibold text-primary' : 'font-medium text-muted',
          )}
        >
          <span className="inline-flex">
            {hasCommented ? <ActiveCommentIcon size={14} /> : <CommentIcon size={14} />}
          </span>
          <span>
            {commentCount} comment{commentCount === 1 ? '' : 's'}
          </span>
        </motion.button>
      </div>
    </section>
  )
}

export default React.memo(Post, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.post.id === nextProps.post.id &&
    prevProps.post.text === nextProps.post.text &&
    prevProps.post.images?.length === nextProps.post.images?.length &&
    prevProps.post.reactionCounts?.like === nextProps.post.reactionCounts?.like &&
    prevProps.post.reactionCounts?.comment === nextProps.post.reactionCounts?.comment &&
    prevProps.post.ownReactions?.like?.length === nextProps.post.ownReactions?.like?.length &&
    prevProps.showFullContent === nextProps.showFullContent &&
    prevProps.isPublic === nextProps.isPublic
  )
})

export type { PostProps }
